import DeleteIconButton from '../components/DeleteIconButton';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { Box, Button, Checkbox, FormControlLabel, Grid, IconButton, Switch, TextField, Typography } from '@mui/material';
import QRCode from 'qrcode.react';
import PropTypes from 'prop-types';
import * as React from 'react';

import AdditionalExternalIDManager from '../components/AdditionalExternalIDManager';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import TripDefaultSettings from '../components/TripDefaultSettings';
import UploadImageButton from '../components/UploadImageButton';
import { ContextStore } from '../store/contextStore';
import { copyTextToClipboard } from '../utils/clipboard';
import { operatorAppliesDefaultTagCategories } from '../utils/tagCategories';
import TripDepartures from './TripDepartures';

const MEETING_POINT_IMAGE_HEIGHT = 180;

// CP-7968: the text under "Use Tour Operator default settings". Where the operator-wide
// setting (ops console › Trip Defaults) is on, the default tag categories apply to every trip
// whatever this switch says, so the switch can be off while the tag categories below show
// "Inherited" -- the text has to say why. A trip's own tag category row always wins over the
// default for that category (backend get_effective_tag_categories).
const OWN_ROW_WINS = 'Where this trip has its own row for a tag category, that row is used instead of the default.';

export function tripSettingsSwitchHelp(usesDefaults, operatorAppliesDefaults) {
    if (usesDefaults) {
        return [
            'This trip uses the Tour Operator default settings and tag categories.',
            OWN_ROW_WINS,
            'Turn this off to customize the settings for this trip.',
            operatorAppliesDefaults
                ? 'The default tag categories stay either way: this operator applies them to every trip.'
                : null,
        ].filter(Boolean).join(' ');
    }
    if (operatorAppliesDefaults) {
        return [
            'This operator applies the Tour Operator default tag categories to every trip, whatever this switch says: they show as Inherited below.',
            OWN_ROW_WINS,
            'Turn this on to use the default settings as well.',
        ].join(' ');
    }
    return 'This trip uses its own settings and tag categories. Turn this on to use the Tour Operator defaults.';
}

function DownloadQRCode(props) {
    const downloadQRCode = () => {
        const qrCodeURL = document.getElementById('qrCodeEl').toDataURL('image/png').replace('image/png', 'image/octet-stream');
        let aEl = document.createElement('a');
        aEl.href = qrCodeURL;
        aEl.download = 'QR_Code.png';
        document.body.appendChild(aEl);
        aEl.click();
        document.body.removeChild(aEl);
    };

    return (
        <>
            <QRCode id="qrCodeEl" size={150} value={props.value} />
            <br />
            <Button color="primary" variant="outlined" onClick={downloadQRCode}>
                Download
            </Button>
        </>
    );
}

DownloadQRCode.propTypes = {
    value: PropTypes.string.isRequired,
};

const TRIP_BELOW_FIRST_BOX_KEYS = [
    'use_operator_defaults',
    'tag_categories',
    'tag_categories_default',
    'atag',
    'tags',
    'location_sharing',
    'mytrips_display',
    'has_group_chat',
    'exclude_from_chat',
    'disable_internal_chat',
    'include_party_members_in_chat',
    'meeting_point',
    'departures',
    'has_departures',
    'is_bespoke',
    'custom_id',
    'additional_custom_ids',
    'allow_selfreg',
    'use_as_template',
    'review',
    'photo_sharing',
    'context',
    'crosssells',
];

function tripShallowEqualForBelowFirstBox(a, b) {
    if (a === b) {
        return true;
    }
    if (!a || !b) {
        return false;
    }
    for (let i = 0; i < TRIP_BELOW_FIRST_BOX_KEYS.length; i += 1) {
        const k = TRIP_BELOW_FIRST_BOX_KEYS[i];
        if (a[k] !== b[k]) {
            return false;
        }
    }
    return true;
}

function tripEditInfoBelowFirstBoxPropsEqual(prev, next) {
    return (
        prev.srLink === next.srLink &&
        prev.clientErrors === next.clientErrors &&
        prev.isSmallScreen === next.isSmallScreen &&
        prev.syncActive === next.syncActive &&
        prev.isTouched === next.isTouched &&
        tripShallowEqualForBelowFirstBox(prev.trip, next.trip)
    );
}

function TripEditInfoBelowFirstBox(props) {
    const {
        trip,
        classes,
        hasWritePerm,
        hasPerm,
        history,
        onTourChange,
        clientErrors,
        clientErrorsFromParent,
        setClientErrors,
        isSmallScreen,
        srLink,
        isTouched,
        meetingPointPhotoDz,
        handleLocationRadioChange,
        onSwitchChange,
        onUseOperatorDefaultsToggle,
        onTextChange,
        onMeetingPointChange,
        checkMeetingPointName,
        checkValidURL,
        handleOnBlur,
        handleMeetingPointPhotoDelete,
        onAdditionalExternalIDChange,
        onAdditionalExternalIDAdd,
        handleRemoveAdditionalCustomId,
        handleReviewChange,
        onCrosssellsChange,
        onContextTextChange,
    } = props;
    const { tourOperator } = React.useContext(ContextStore);

    return (
        <>
            {!trip.is_bespoke && trip.has_departures ? (
                <Box className={classes.styledBox}>
                    <TripDepartures
                        trip={trip}
                        disabled={!hasWritePerm}
                        onChange={onTourChange}
                        clientErrors={clientErrors}
                        setClientErrors={setClientErrors}
                    />
                </Box>
            ) : null}
            <Box className={classes.styledBox}>
                <Grid container direction="row" spacing={2}>
                    <Grid item xs={12} md={8}>
                        <Typography variant="h6">Trip Settings</Typography>
                        <FormControlLabel
                            control={<Switch checked={!!trip.use_operator_defaults} onChange={(e) => onUseOperatorDefaultsToggle(e)} name="use_operator_defaults" color="primary" />}
                            label="Use Tour Operator default settings"
                            disabled={!hasWritePerm}
                        />
                        <Typography variant="body2" sx={{ marginLeft: '3rem' }}>
                            {tripSettingsSwitchHelp(!!trip.use_operator_defaults, operatorAppliesDefaultTagCategories(tourOperator))}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ textAlign: 'right' }}>
                        <Button variant="outlined" onClick={() => history.push('/settings/general/trip-defaults')}>OPEN GENERAL SETTINGS</Button>
                    </Grid>
                </Grid>
                <Grid container direction="row" spacing={2} mt={1}>
                    <TripDefaultSettings
                        mode="trip"
                        hasWritePerm={hasWritePerm}
                        hasUpdateTagCategoriesPerm={hasPerm('tour', 'update_tag_categories')}
                        hideGeneralSettings={!!trip.use_operator_defaults}
                        locationSharingValue={trip.location_sharing}
                        onLocationSharingChange={(e) => handleLocationRadioChange(e)}
                        myTripsDisplayValue={typeof trip.mytrips_display === 'undefined' ? true : trip.mytrips_display}
                        onMyTripsDisplayChange={onSwitchChange}
                        hasGroupChatValue={!!trip.has_group_chat}
                        onHasGroupChatChange={onSwitchChange}
                        excludeFromChatValue={!!trip.exclude_from_chat}
                        onExcludeFromChatChange={onSwitchChange}
                        disableInternalChatValue={!!trip.disable_internal_chat}
                        onDisableInternalChatChange={onSwitchChange}

                        trip={trip}
                        onTripChange={onTourChange}
                        clientErrors={clientErrorsFromParent}
                        isParentTouched={isTouched}
                    />
                </Grid>

                {/* Moved into TripDefaultSettings when mode="trip" */}

                {/* AssociatedTagCategories moved into TripDefaultSettings when mode="trip" */}
            </Box>

            <Box className={classes.styledBox}>
                <Grid container direction="row" spacing={2}>
                    <Grid item xs={12} sm={8} container direction="row" spacing={2}>
                        <Grid item xs={12}>
                            <Typography style={{ fontWeight: 'bold' }}>Meeting Point</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                disabled={!hasWritePerm}
                                variant="filled"
                                size="small"
                                label="Meeting Point Name"
                                name="name"
                                value={trip.meeting_point?.name || ''}
                                fullWidth
                                multiline
                                onChange={onMeetingPointChange}
                                onBlur={checkMeetingPointName}
                                inputProps={{ maxLength: 512 }}
                                error={clientErrorsFromParent.missingMeetingPoint || clientErrorsFromParent.missingMeetingPointName}
                                helperText={clientErrorsFromParent.missingMeetingPointName ? 'Meeting point name is required when Google Maps URL is used' : (clientErrorsFromParent.missingMeetingPoint ? 'Trip name is required' : `${512 - (trip.meeting_point?.name?.length || 0)} characters left`)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                disabled={!hasWritePerm}
                                variant="filled"
                                size="small"
                                label="Meeting Point Address"
                                name="address"
                                value={trip.meeting_point?.address || ''}
                                fullWidth
                                multiline
                                onChange={onMeetingPointChange}
                                onBlur={handleOnBlur}
                                inputProps={{ maxLength: 1024 }}
                                error={clientErrorsFromParent.missingMeetingPoint}
                                helperText={`${1024 - (trip.meeting_point?.address?.length || 0)} characters left`}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField disabled={!hasWritePerm} variant="filled" size="small" label="Meeting Point Latitude" name="latitude" value={trip.meeting_point?.latitude ?? ''} fullWidth onChange={onMeetingPointChange} onBlur={handleOnBlur} inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField disabled={!hasWritePerm} variant="filled" size="small" label="Meeting Point Longitude" name="longitude" value={trip.meeting_point?.longitude ?? ''} fullWidth onChange={onMeetingPointChange} onBlur={handleOnBlur} inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                disabled={!hasWritePerm}
                                variant="filled"
                                size="small"
                                label="Meeting Point Comment"
                                name="comment"
                                value={trip.meeting_point?.comment || ''}
                                fullWidth
                                multiline
                                onChange={onMeetingPointChange}
                                onBlur={handleOnBlur}
                                inputProps={{ maxLength: 1024 }}
                                error={clientErrorsFromParent.missingMeetingPoint}
                                helperText={`${1024 - (trip.meeting_point?.comment?.length || 0)} characters left`}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                disabled={!hasWritePerm}
                                variant="filled"
                                size="small"
                                label="Google Maps URL"
                                name="maps_url"
                                value={trip.meeting_point?.maps_url || ''}
                                fullWidth
                                multiline
                                onChange={onMeetingPointChange}
                                inputProps={{ maxLength: 1024 }}
                                onBlur={checkValidURL}
                                error={clientErrorsFromParent.invalidURLMeetingPointURL}
                                helperText={clientErrorsFromParent.invalidURLMeetingPointURL ? 'Please use a valid URL' : `${1024 - (trip.meeting_point?.maps_url?.length || 0)} characters left`}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography style={{ fontWeight: 'bold' }}>Meeting Point Image</Typography>
                            <input {...meetingPointPhotoDz.getInputProps()} />
                            {/* CP-5556: [UPLOAD IMAGE] / [REPLACE IMAGE] on the image, trash on its top-right corner. The empty
                                placeholder is the same size as an uploaded image (it used to be 65vh tall), so the mountain is
                                fitted inside it rather than cropped. */}
                            <div style={{ position: 'relative' }}>
                                <UploadImageButton
                                    rootStyle={{ width: '100%', height: MEETING_POINT_IMAGE_HEIGHT, overflow: 'hidden' }}
                                    imageStyle={{ width: '100%', height: '100%' }}
                                    placeholderStyle={{ width: '100%', height: '100%', backgroundSize: 'contain', backgroundColor: '#F5F5F5' }}
                                    height={'100%'}
                                    width={'100%'}
                                    imageAction="overlay"
                                    borderRadius={10}
                                    onClick={meetingPointPhotoDz.open}
                                    isUpload={!(trip.meeting_point?.photo && trip.meeting_point?.photo.p && trip.meeting_point?.photo.s)}
                                    image={trip.meeting_point?.photo}
                                    disabled={!hasWritePerm}
                                />
                                {trip.meeting_point?.photo && (
                                    <span style={{ position: 'absolute', top: 11, right: 11, zIndex: 10 }}>
                                        <DeleteIconButton title="Delete image" overImage allowed={hasWritePerm} onClick={() => handleMeetingPointPhotoDelete()} />
                                    </span>
                                )}
                            </div>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>

            {/* Trip Dates section removed */}
            <Box className={classes.styledBox}>
                <Grid container direction="row" spacing={2} alignItems="flex-start">
                    <Grid item xs={12} sm={6} container direction="row" spacing={2} pr={!isSmallScreen && 14} className={classes.fieldsColumn}>
                        <Grid item xs={12}>
                            <Typography style={{ fontWeight: 'bold' }}>External IDs</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                disabled={!hasWritePerm}
                                variant="filled"
                                size="small"
                                label="Primary External ID"
                                name="custom_id"
                                value={trip.custom_id || ''}
                                fullWidth
                                onChange={onTextChange}
                                helperText={
                                    <span>
                                        e.g. <strong>P0TPGX</strong> or <strong>P0TPGX/14:00</strong>. Optional field. Corresponds to the primary ID of a product
                                        in your booking system. Primary ID cannot be associated with tags or related tours,
                                        use auxiliary external IDs to do that.
                                        <br />
                                        External IDs can include departure time suffixes. E.g., suppose that there is a product
                                        with ID <strong>ABCDEF</strong> which departs daily at <strong>9 AM</strong> and <strong>1 PM</strong>.
                                        You can use <strong>ABCDEF/09:00</strong> as your external ID to only match bookings for <strong>9 AM</strong>.
                                        With auxiliary external IDs, you can assign different tags to different departure times this way.
                                    </span>
                                }
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <AdditionalExternalIDManager
                                data={trip.additional_custom_ids}
                                onChange={onAdditionalExternalIDChange}
                                onAdd={onAdditionalExternalIDAdd}
                                onDelete={(aeid, idx) => handleRemoveAdditionalCustomId(idx)}
                                hasWritePerm={hasWritePerm}
                                tagCategories={trip.tag_categories}
                            />
                        </Grid>
                        {/* <Grid item xs={12}>
                            <Typography style={{ fontWeight: 'bold' }}>Additional Custom IDs</Typography>
                        </Grid>
                        {trip.additional_custom_ids &&
                            trip.additional_custom_ids.length > 0 &&
                            trip.additional_custom_ids.sort(compare).map((aci) => {
                                return (
                                    <Grid item xs={12} container direction="col" key={aci.idx}>
                                        <Grid item xs={12} container>
                                            <Grid item xs={10}>
                                                <TextField disabled={!hasWritePerm} variant="filled" size="small" label="Custom ID" name="additional_custom_id" value={aci.custom_id || ''} fullWidth onBlur={handleOnBlur} onChange={(ev) => onAdditionalCustomIdChange(ev, aci.idx)} />
                                            </Grid>
                                            {hasWritePerm && <Grid item xs={2}>
                                                <Button fullWidth style={{ height: '100%', color: 'black' }} onClick={() => handleRemoveAdditionalCustomId(aci.idx)}>
                                                    <DeleteIcon color="primary" fontSize="small" onClick={() => handleRemoveAdditionalCustomId(aci.idx)} />
                                                </Button>
                                            </Grid>}
                                        </Grid>
                                        <Grid xs={12}>
                                            <RemoteAutocomplete
                                                disabled={!hasWritePerm}
                                                multiple
                                                apiFilters={[{ field: 'category', op: 'eq', value: [...trip?.tag_categories?.map((cat) => cat?.category?.id?.toString())] }]}
                                                getOptionLabel={(option) => option.category?.code + ': ' + option.name }
                                                resource="tour_tag"
                                                urlSuffix={null}
                                                label="Choose Tags"
                                                variant="filled"
                                                size="small"
                                                onChange={(ev, nt) => onAdditionalCustomIdTagChange(ev, nt, aci.idx, trip.additional_custom_ids)}
                                                value={aci?.tour_user_tags}
                                            />
                                        </Grid>
                                    </Grid>
                                );
                            })}
                        {clientErrorsFromParent.missingAdditionalCustomIDsValues && (
                            <Grid item xs={12}>
                                <Typography color="secondary">Custom IDs must have values</Typography>
                            </Grid>
                        )}
                        {hasWritePerm && <Grid item xs={12}>
                            <Button variant="outlined" color="primary" onClick={handleAddAdditionalCustomId}>
                                Add new Custom Id
                            </Button>
                        </Grid>} */}
                        {/* <Grid item xs={12}>
                            <TextField
                                disabled={!hasWritePerm}
                                variant="filled"
                                size="small"
                                label="Trip Code"
                                name="booking_number"
                                value={trip.booking_number || ''}
                                fullWidth
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <IconButton
                                            aria-label="copy booking number to clipboard"
                                            onClick={() => {
                                                copyTextToClipboard(trip.booking_number);
                                            }}
                                            edge="end"
                                        >
                                            <FileCopyIcon />
                                        </IconButton>
                                    ),
                                }}
                            />
                        </Grid> */}
                        <Grid item xs={12}>
                            <Grid item xs={12} container alignItems="center">
                                <Grid item xs={1}>
                                    <FormControlLabel disabled={!hasWritePerm} control={<Checkbox checked={trip.allow_selfreg} onChange={onSwitchChange} name="allow_selfreg" color="primary" />} />
                                </Grid>
                                <Grid item xs={10} ml={isSmallScreen ? 0 : -1}>
                                    <Typography style={{ fontWeight: 'bold' }}>Create Self-Registration Page</Typography>
                                </Grid>
                            </Grid>
                            {trip.allow_selfreg && (
                                <>
                                    <Grid item xs={12}>
                                        <TextField
                                            disabled={!hasWritePerm}
                                            variant="filled"
                                            size="small"
                                            label="Self-Registration Link"
                                            name="self_registration_link"
                                            value={srLink || ''}
                                            fullWidth
                                            InputProps={{
                                                readOnly: true,
                                                endAdornment: (
                                                    <IconButton
                                                        aria-label="copy self registration link to clipboard"
                                                        onClick={() => {
                                                            copyTextToClipboard(srLink);
                                                        }}
                                                        edge="end"
                                                    >
                                                        <FileCopyIcon />
                                                    </IconButton>
                                                ),
                                            }}
                                            helperText="Anyone who has this link can join the tour"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box pb={2} pt={2}>
                                            {srLink && <DownloadQRCode value={srLink} />}
                                        </Box>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Grid>
                    <Grid item xs={12} sm={6} container direction="row" spacing={4}>
                        <Grid item xs={12}>
                            <FormControlLabel disabled={!hasWritePerm} control={<Switch checked={trip.use_as_template} onChange={onSwitchChange} name="use_as_template" color="primary" />} label="Use as Template for Future Tours" />
                        </Grid>
                    </Grid>
                </Grid>
            </Box>

            <Box className={classes.styledBox}>
                <Grid container direction="row" spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="h6">Other</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography fontWeight={500}>Review</Typography>
                    </Grid>
                    <Grid item xs={12} lg={8} container alignItems="flex-start" spacing={2}>
                        <Grid item xs={6} lg={3}>
                            <TextField disabled={!hasWritePerm} variant="filled" size="small" label="Review Source" fullWidth value={trip.review?.name || ''} name="review_name" onChange={handleReviewChange} InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={6} lg={9}>
                            <TextField
                                disabled={!hasWritePerm}
                                variant="filled"
                                size="small"
                                label="Review Source Link"
                                required
                                fullWidth
                                value={trip.review?.url || ''}
                                name="review_link"
                                onChange={handleReviewChange}
                                InputLabelProps={{ shrink: true }}
                                onBlur={checkValidURL}
                                error={clientErrorsFromParent.invalidURLReview}
                                helperText={clientErrorsFromParent.invalidURLReview && 'Please use a valid URL'}
                            />
                        </Grid>
                    </Grid>

                    <Grid item xs={12} mt={2}>
                        <Typography fontWeight={500}>Photo Sharing</Typography>
                    </Grid>
                    <Grid item xs={12} lg={8} container alignItems="flex-start" spacing={2}>
                        <Grid item xs={6} lg={3}>
                            <TextField disabled={!hasWritePerm} variant="filled" size="small" label="Photo Sharing Source" fullWidth value={trip.photo_sharing?.name || ''} name="photo_sharing_name" onChange={handleReviewChange} InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={6} lg={9}>
                            <TextField
                                disabled={!hasWritePerm}
                                variant="filled"
                                size="small"
                                label="Photo Sharing Link"
                                required
                                fullWidth
                                value={trip.photo_sharing?.url || ''}
                                name="photo_sharing_link"
                                onChange={handleReviewChange}
                                InputLabelProps={{ shrink: true }}
                                onBlur={checkValidURL}
                                error={clientErrorsFromParent.invalidURLPhotoSharing}
                                helperText={clientErrorsFromParent.invalidURLPhotoSharing && 'Please use a valid URL'}
                            />
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={8} mt={2}>
                        <Typography fontWeight={500}>Packing Items</Typography>
                    </Grid>
                    {/* <Box display="flex" flexGrow={1} style={{ width: '100%', padding: '0.2rem' }}>
                            <Editor
                                apiKey="gnxklgjsxcc59dvzactz5z0q8sy2e4rgefa44ge6jagp2y1t"
                                onInit={(evt, editor) => setEditorRef('packing_items', editor)}
                                initialValue={editorInitialValues?.packing_items}
                                fullWidth
                                onDirty={_.throttle(() => onContextSaveDirty('packing_items'), 500)}
                                init={{ ...DEFAULT_EDITOR_OPTIONS, placeholder: 'Packing Items' }}
                            />
                        </Box> */}
                    <Grid item xs={12} md={8}>
                        <TextField disabled={!hasWritePerm} variant="filled" size="small" label="Packing Items" fullWidth multiline minRows={4} value={trip.context?.packing_items || ''} name="packing_items" onChange={onContextTextChange} />
                    </Grid>

                    <Grid item xs={12} mt={2}>
                        <Typography fontWeight={500}>Additional Information</Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <TextField disabled={!hasWritePerm} name="tour_description" value={trip.context?.tour_description || ''} label="Additional Information" variant="filled" size="small" fullWidth multiline minRows={4} onChange={onContextTextChange} />
                    </Grid>
                    {/* <Grid item xs={12}>
                        <Box display="flex" flexGrow={1} style={{ width: '100%', padding: '0.2rem' }}>
                            <Editor
                                apiKey="gnxklgjsxcc59dvzactz5z0q8sy2e4rgefa44ge6jagp2y1t"
                                onInit={(evt, editor) => setEditorRef('tour_description', editor)}
                                initialValue={editorInitialValues?.tour_description}
                                fullWidth
                                onDirty={_.throttle(() => onContextSaveDirty('tour_description'), 500)}
                                init={{ ...DEFAULT_EDITOR_OPTIONS, placeholder: 'Tour Description' }}
                            />
                        </Box>
                    </Grid> */}

                    <Grid item xs={12} mt={2}>
                        <Typography fontWeight={500}>Local Recommendations</Typography>
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <TextField
                            disabled={!hasWritePerm}
                            variant="filled"
                            size="small"
                            label="Link"
                            fullWidth
                            value={trip.context?.local_recommendations_link || ''}
                            name="local_recommendations_link"
                            onChange={onContextTextChange}
                            InputLabelProps={{ shrink: true }}
                            onBlur={checkValidURL}
                            error={clientErrorsFromParent.local_recommendations_link}
                            helperText={clientErrorsFromParent.local_recommendations_link && 'Please use a valid URL'}
                        />
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <TextField disabled={!hasWritePerm} name="local_recommendations" value={trip.context?.local_recommendations || ''} label="Local Recommendations" variant="filled" size="small" fullWidth multiline minRows={4} onChange={onContextTextChange} />
                    </Grid>

                    <Grid item xs={12} mt={2}>
                        <Typography fontWeight={500}>Inclusions</Typography>
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <TextField disabled={!hasWritePerm} name="inclusions" value={trip.context?.inclusions || ''} label="Inclusions" variant="filled" size="small" fullWidth multiline minRows={4} onChange={onContextTextChange} />
                    </Grid>

                    <Grid item xs={12} mt={2}>
                        <Typography fontWeight={500}>Exclusions</Typography>
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <TextField disabled={!hasWritePerm} name="exclusions" value={trip.context?.exclusions || ''} label="Exclusions" variant="filled" size="small" fullWidth multiline minRows={4} onChange={onContextTextChange} />
                    </Grid>

                    <Grid item xs={12} mt={2}>
                        <Typography fontWeight={500}>Destination</Typography>
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <TextField disabled={!hasWritePerm} name="destination" value={trip.context?.destination || ''} label="Destination" variant="filled" size="small" fullWidth multiline minRows={4} onChange={onContextTextChange} />
                    </Grid>

                    <Grid item xs={12} mt={2}>
                        <Typography fontWeight={500}>Highlights</Typography>
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <TextField disabled={!hasWritePerm} name="highlights" value={trip.context?.highlights || ''} label="Highlights" variant="filled" size="small" fullWidth multiline minRows={4} onChange={onContextTextChange} />
                    </Grid>

                    <Grid item xs={12} mt={2}>
                        <Typography fontWeight={500}>Redemption Instructions</Typography>
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <TextField disabled={!hasWritePerm} name="redemption_instructions" value={trip.context?.redemption_instructions || ''} label="Redemption Instructions" variant="filled" size="small" fullWidth multiline minRows={4} onChange={onContextTextChange} />
                    </Grid>

                    <Grid item xs={12} mt={2}>
                        <Typography fontWeight={500}>Booking Terms</Typography>
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <TextField disabled={!hasWritePerm} name="booking_terms" value={trip.context?.booking_terms || ''} label="Booking Terms" variant="filled" size="small" fullWidth multiline minRows={4} onChange={onContextTextChange} />
                    </Grid>

                    <Grid item xs={12} mt={2}>
                        <Typography fontWeight={500}>Usage Instructions</Typography>
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <TextField disabled={!hasWritePerm} name="usage_instructions" value={trip.context?.usage_instructions || ''} label="Usage Instructions" variant="filled" size="small" fullWidth multiline minRows={4} onChange={onContextTextChange} />
                    </Grid>

                    <Grid item xs={12} mt={2}>
                        <Typography fontWeight={500}>Cancellation policy</Typography>
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <TextField
                            disabled={!hasWritePerm}
                            variant="filled"
                            size="small"
                            label="Link"
                            fullWidth
                            value={trip.context?.cancellation_policy_link || ''}
                            name="cancellation_policy_link"
                            onChange={onContextTextChange}
                            InputLabelProps={{ shrink: true }}
                            onBlur={checkValidURL}
                            error={clientErrorsFromParent.cancellation_policy_link}
                            helperText={clientErrorsFromParent.cancellation_policy_link && 'Please use a valid URL'}
                        />
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <TextField disabled={!hasWritePerm} name="cancellation_policy" value={trip.context?.cancellation_policy || ''} label="Description" variant="filled" size="small" fullWidth multiline minRows={4} onChange={onContextTextChange} />
                    </Grid>

                    <Grid item xs={12} mt={2}>
                        <Typography fontWeight={500}>Operated by</Typography>
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <TextField disabled={!hasWritePerm} variant="filled" size="small" label="Name" fullWidth value={trip.context?.operated_by || ''} name="operated_by" onChange={onContextTextChange} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <TextField disabled={!hasWritePerm} name="operated_by_description" value={trip.context?.operated_by_description || ''} label="Description" variant="filled" size="small" fullWidth multiline minRows={4} onChange={onContextTextChange} />
                    </Grid>

                    <Grid item xs={12} mt={2}>
                        <Typography fontWeight={500}>Tour type</Typography>
                    </Grid>

                    <Grid item xs={12} lg={8}>
                        <TextField disabled={!hasWritePerm} name="tour_type" value={trip.context?.tour_type || ''} label="Tour type" variant="filled" size="small" fullWidth onChange={onContextTextChange} />
                    </Grid>

                    <Grid item xs={12} mt={2}>
                        <Typography fontWeight={500}>Special instructions/Notes</Typography>
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <TextField
                            disabled={!hasWritePerm}
                            variant="filled"
                            size="small"
                            label="Link"
                            fullWidth
                            value={trip.context?.notes_link || ''}
                            name="notes_link"
                            onChange={onContextTextChange}
                            InputLabelProps={{ shrink: true }}
                            onBlur={checkValidURL}
                            error={clientErrorsFromParent.notes_link}
                            helperText={clientErrorsFromParent.notes_link && 'Please use a valid URL'}
                        />
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <TextField disabled={!hasWritePerm} name="notes" value={trip.context?.notes || ''} label="Notes" variant="filled" size="small" fullWidth onChange={onContextTextChange} />
                    </Grid>

                    <Grid item xs={12} mt={2}>
                        <Typography fontWeight={500}>Upsell</Typography>
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <TextField
                            disabled={!hasWritePerm}
                            variant="filled"
                            size="small"
                            label="Upsell Link"
                            fullWidth
                            value={trip.context?.upsell_link || ''}
                            name="upsell_link"
                            onChange={onContextTextChange}
                            InputLabelProps={{ shrink: true }}
                            onBlur={checkValidURL}
                            error={clientErrorsFromParent.upsell_link}
                            helperText={clientErrorsFromParent.upsell_link && 'Please use a valid URL'}
                        />
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <TextField disabled={!hasWritePerm} name="upsell_text" value={trip.context?.upsell_text || ''} label="Upsell Text" variant="filled" size="small" fullWidth onChange={onContextTextChange} />
                    </Grid>

                    <Grid item xs={12} mt={2}>
                        <Typography fontWeight={500}>Cross-sells</Typography>
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <RemoteAutocomplete
                            disabled={!hasWritePerm}
                            InputProps={{ required: true }}
                            isOptionEqualToValue={(opt, val) => opt.id === val.id}
                            resource="itinerary"
                            urlSuffix={null}
                            label="Select itineraries"
                            multiple
                            variant="filled"
                            size="small"
                            onChange={onCrosssellsChange}
                            value={trip.crosssells || ''}
                            getOptionLabel={(opt) => {
                                return opt.name;
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>

        </>
    );
}

export default React.memo(TripEditInfoBelowFirstBox, tripEditInfoBelowFirstBoxPropsEqual);
